"use client";

import React, { useEffect, useState } from "react";
import clsx from "clsx";
import type { ChatEntitySearchResultsEmbed } from "./types";
import { entitySearchMatchSummary, isDeliverableSearchEmbed } from "./entitySearchResultsUi";
import { SearchEmbedCandidateList } from "./SearchEmbedCandidateList";
import { AgenticToolCallRow } from "@/components/ui/agentic-tool-call-row";

type EntitySearchResultsCardProps = {
  embed: ChatEntitySearchResultsEmbed;
  cardIndex: number;
};

export function EntitySearchResultsCard({ embed, cardIndex }: EntitySearchResultsCardProps) {
  const deliverable = isDeliverableSearchEmbed(embed);
  const [expanded, setExpanded] = useState(deliverable);

  useEffect(() => {
    setExpanded(deliverable);
  }, [deliverable, cardIndex]);

  const summary = entitySearchMatchSummary(embed);
  const candidateList = <SearchEmbedCandidateList embeds={[embed]} />;

  if (deliverable) {
    return (
      <div
        data-testid="chat-entity-search-results"
        data-presentation="deliverable"
        className={clsx(
          "rounded-lg border border-slate-200/70 bg-slate-50/90 p-2 dark:border-slate-700/55 dark:bg-slate-800/45",
        )}
      >
        <div className="mb-1 flex items-center gap-1.5 px-0.5">
          <span className="text-[11px] font-medium text-slate-600 dark:text-slate-300">{embed.resource_label}</span>
          <span className="text-[11px] text-slate-500 dark:text-slate-400">{summary}</span>
        </div>
        {candidateList}
      </div>
    );
  }

  return (
    <div
      data-testid="chat-entity-search-results"
      data-presentation="step"
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      className="cursor-pointer rounded-md outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40"
      onClick={() => setExpanded((open) => !open)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          setExpanded((open) => !open);
        }
      }}
    >
      <AgenticToolCallRow
        toolName={embed.entity_type}
        label={embed.resource_label}
        status="success"
        summary={summary}
        hidePrefix
        className="!pl-0"
        detail={expanded ? candidateList : null}
      />
    </div>
  );
}
